import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Leaf, Gem, Heart, Star, Gamepad2, Calendar, BookOpen } from 'lucide-react';
import { LOGO, IMAGES, PRODUCTS, INTENTIONS, SERVICES, BRAND, ABOUT } from '../mock';
import { fetchSettings, fetchServices, fetchProduct } from '../api';
import ProductCard from '../components/ProductCard';

const Stars = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {Array.from({ length: 26 }).map((_, i) => (
      <span key={i} className="absolute rounded-full bg-[#f0d582] twinkle" style={{ top: `${Math.random() * 100}%`, left: `${Math.random() * 100}%`, width: `${Math.random() * 3 + 1}px`, height: `${Math.random() * 3 + 1}px`, animationDelay: `${Math.random() * 3}s` }} />
    ))}
  </div>
);

const Home = () => {
  const featured = PRODUCTS.filter((p) => p.bestseller).slice(0, 4);
  const topServices = SERVICES.slice(0, 3);
  const [featuredId, setFeaturedId] = useState('spiritual-bath');
  const [liveServices, setLiveServices] = useState(SERVICES);
  const [bundle, setBundle] = useState(null);
  useEffect(() => {
    fetchSettings().then((s) => s?.featuredServiceId && setFeaturedId(s.featuredServiceId)).catch(() => {});
    fetchServices().then((d) => Array.isArray(d) && d.length && setLiveServices(d.map((s) => ({ ...s, duration: s.durationMin ? `${s.durationMin} min` : s.duration })))).catch(() => {});
    fetchProduct('royal-roots-complete-hair-set').then((p) => p && p.id && setBundle(p)).catch(() => {});
  }, []);
  const featuredService = liveServices.find((s) => s.id === featuredId) || liveServices[0];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <Stars />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-[#d6197d]/15 blur-[120px] glow-pulse" />
        <div className="absolute bottom-0 right-0 h-[400px] w-[400px] rounded-full bg-[#d4af37]/10 blur-[120px]" />
        <div className="relative z-10 text-center px-5 max-w-4xl fade-up">
          <img src={LOGO} alt="Prestige Elements" className="h-40 w-40 md:h-52 md:w-52 mx-auto rounded-full ring-1 ring-[#d4af37]/40 floaty" />
          <p className="font-jost text-xs md:text-sm tracking-[0.5em] text-[#ff4da6] uppercase mt-8 mb-4">Beauty · Wellness · Energy</p>
          <h1 className="font-cinzel text-4xl md:text-7xl leading-tight text-gold-gradient">Radiate Beauty<br />From The Inside Out</h1>
          <p className="font-cormorant text-xl md:text-2xl text-[#f5ecd9]/70 max-w-2xl mx-auto mt-6">A house of natural skincare, body oils, healing crystals, and spiritual wellness — crafted to cleanse, balance, and restore. Established 2019.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
            <Link to="/shop" className="btn-gold flex items-center gap-2 rounded-full px-9 py-4 text-sm font-semibold tracking-wide text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><Sparkles className="h-4 w-4" /> Shop Prestige</Link>
            <Link to="/book" className="flex items-center gap-2 rounded-full px-9 py-4 text-sm font-medium tracking-wide text-[#f0d582] border border-[#d4af37]/40 hover:bg-[#d4af37]/10 transition-colors">Book A Healing <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <p className="font-jost text-xs text-[#f5ecd9]/50 mt-6 tracking-widest">✦ {BRAND.promo} ✦</p>
        </div>
      </section>

      {/* VALUE PILLARS */}
      <section className="py-16 border-y border-[#d4af37]/15 bg-[#0d0a0c]">
        <div className="max-w-6xl mx-auto px-5 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Leaf, t: 'Pure Botanicals', d: 'Real flower essence & essential oils' },
            { icon: Gem, t: 'Healing Crystals', d: 'Aligned to each energy center' },
            { icon: Heart, t: 'Spiritual Wellness', d: 'Readings, baths & energy work' },
            { icon: Star, t: 'Since 2019', d: 'Handcrafted with intention' },
          ].map((p, i) => (
            <div key={i} className="text-center">
              <div className="h-14 w-14 mx-auto rounded-full border border-[#d4af37]/30 flex items-center justify-center mb-4"><p.icon className="h-6 w-6 text-[#f0d582]" /></div>
              <h3 className="font-cinzel text-sm tracking-wider text-[#f5ecd9] mb-1">{p.t}</h3>
              <p className="font-jost text-xs text-[#f5ecd9]/50">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">From The Apothecary</p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient">Featured Products</h2>
            <div className="gold-divider mt-4" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
            {featured.map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
          <div className="text-center mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 text-[#f0d582] border-b border-[#d4af37]/50 pb-1 hover:text-[#ff4da6] transition-colors font-jost text-sm tracking-widest uppercase">Shop All Products <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* FEATURED BUNDLE */}
      {bundle && (
        <section className="relative py-20 overflow-hidden bg-[#0d0a0c]" data-testid="home-bundle-feature">
          <div className="absolute top-1/2 left-0 -translate-y-1/2 h-[360px] w-[360px] rounded-full bg-[#d6197d]/15 blur-[120px]" />
          <div className="relative max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-10 items-center">
            <Link to={`/product/${bundle.id}`} className="relative rounded-2xl overflow-hidden card-glass group">
              <img src={bundle.image} alt={bundle.name} className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
              {bundle.compareAt > bundle.price && (
                <span className="absolute top-4 left-4 text-[11px] tracking-[0.15em] uppercase px-3 py-1.5 rounded-full bg-gradient-to-r from-[#d6197d] to-[#ff4da6] text-white font-jost">Save ${(bundle.compareAt - bundle.price).toFixed(0)}</span>
              )}
            </Link>
            <div>
              <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">The Complete Ritual</p>
              <h2 className="font-cinzel text-3xl md:text-4xl text-gold-gradient leading-tight">{bundle.name}</h2>
              <p className="font-cormorant text-xl text-[#f5ecd9]/75 leading-relaxed mt-4">{bundle.summary || bundle.description}</p>
              <div className="flex items-baseline gap-3 mt-6">
                <span className="font-cinzel text-3xl text-gold-gradient">${bundle.price.toFixed(2)}</span>
                {bundle.compareAt > bundle.price && (<span className="text-lg text-[#f5ecd9]/40 line-through">${bundle.compareAt.toFixed(2)}</span>)}
              </div>
              <Link to={`/product/${bundle.id}`} data-testid="home-bundle-cta" className="btn-gold inline-flex items-center gap-2 mt-7 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><Sparkles className="h-4 w-4" /> Shop The Set</Link>
            </div>
          </div>
        </section>
      )}

      {/* SHOP BY INTENTION */}
      <section className="py-20 bg-[#0d0a0c]">
        <div className="max-w-6xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Find Your Energy</p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient">Shop By Intention</h2>
            <div className="gold-divider mt-4" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {INTENTIONS.map((i) => (
              <Link key={i.id} to={`/shop?intention=${i.id}`} className="card-glass rounded-2xl p-6 text-center hover-lift group">
                <Sparkles className="h-6 w-6 text-[#f0d582] mx-auto mb-3 transition-transform group-hover:scale-125" />
                <h4 className="font-cinzel text-lg text-[#f5ecd9]">{i.label}</h4>
                <p className="font-jost text-[10px] uppercase tracking-widest text-[#f5ecd9]/40 mt-1">{i.crystal}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* BALI MANGO ROSE FEATURE */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.spa1} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0708] via-[#0a0708]/85 to-[#0a0708]/40" />
        </div>
        <div className="relative max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-4">Our Newest Line</p>
            <h2 className="font-cinzel text-4xl md:text-6xl text-gold-gradient leading-tight">Bali Mango Rose</h2>
            <p className="font-cormorant text-2xl text-[#f5ecd9]/80 mt-4">With real flower essence & essential oil.</p>
            <p className="font-jost text-[#f5ecd9]/60 mt-4 max-w-md leading-relaxed">A warm, radiant body oil that opens the heart and lingers like a sacred memory. Drench yourself in the beauty of nature.</p>
            <Link to="/product/bali-mango-rose-body-oil" className="btn-gold inline-flex items-center gap-2 mt-8 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d6197d] to-[#ff4da6]">Discover The Scent <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-[#d6197d]/20 blur-3xl rounded-full glow-pulse" />
            <img src={PRODUCTS.find((p) => p.id === 'bali-mango-rose-body-oil').image} alt="Bali Mango Rose Body Oil" className="relative rounded-2xl w-full aspect-square object-cover ring-1 ring-[#d4af37]/30 bg-[#120d10]" />
          </div>
        </div>
      </section>

      {/* FEATURED RITUAL */}
      {featuredService && (
        <section data-testid="featured-ritual" className="relative py-24 overflow-hidden">
          <div className="max-w-6xl mx-auto px-5">
            <div className="grid md:grid-cols-2 gap-10 items-center card-glass rounded-3xl overflow-hidden">
              <div className="aspect-[4/3] md:aspect-auto md:h-full overflow-hidden bg-[#120d10]">
                <img src={featuredService.image} alt={featuredService.name} className="w-full h-full object-cover" style={{ minHeight: '340px' }} />
              </div>
              <div className="p-8 md:p-12">
                <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Featured Ritual</p>
                <h2 className="font-cinzel text-3xl md:text-4xl text-gold-gradient leading-tight">{featuredService.name}</h2>
                <div className="gold-divider my-5" style={{ marginLeft: 0 }} />
                <p className="font-cormorant text-xl text-[#f5ecd9]/75 leading-relaxed">{featuredService.summary}</p>
                <div className="flex items-center gap-4 mt-6">
                  <span className="font-cinzel text-2xl text-gold-gradient">{featuredService.priceLabel}</span>
                  <span className="font-jost text-xs text-[#f5ecd9]/50">{featuredService.duration} · In person</span>
                </div>
                <Link to={`/book?service=${featuredService.id}`} data-testid="featured-ritual-book-btn" className="btn-gold inline-flex items-center gap-2 mt-7 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><Calendar className="h-4 w-4" /> Book This Ritual</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* HEALING & SERVICES */}
      <section className="py-24 bg-[#0d0a0c]">
        <div className="max-w-7xl mx-auto px-5">
          <div className="text-center mb-12">
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">{BRAND.motto}</p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient">Healing & Services</h2>
            <div className="gold-divider mt-4" />
            <p className="font-cormorant text-xl text-[#f5ecd9]/60 max-w-2xl mx-auto mt-4">Spiritual baths, chakra work, tarot & oracle readings, guided yoga, and more — book a session to restore your balance.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-7">
            {topServices.map((s) => (
              <div key={s.id} className="card-glass rounded-2xl overflow-hidden hover-lift">
                <div className="aspect-[16/10] overflow-hidden bg-[#120d10]"><img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover" /></div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-cinzel text-xl text-[#f5ecd9]">{s.name}</h3>
                    <span className="font-cinzel text-gold-gradient">{s.priceLabel}</span>
                  </div>
                  <p className="font-jost text-xs text-[#f5ecd9]/50 mb-4">{s.duration} · In person</p>
                  <Link to="/book" className="btn-gold inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-xs font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] to-[#f0d582]"><Calendar className="h-3.5 w-3.5" /> Book Now</Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12 flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/services" className="inline-flex items-center justify-center gap-2 text-[#f0d582] border border-[#d4af37]/40 rounded-full px-8 py-3.5 hover:bg-[#d4af37]/10 transition-colors font-jost text-sm tracking-widest uppercase">Explore Services</Link>
            <Link to="/book" className="btn-gold inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]"><Calendar className="h-4 w-4" /> Book Appointment</Link>
          </div>
        </div>
      </section>

      {/* WELLNESS / EDUCATION */}
      <section className="relative py-24 overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 grid md:grid-cols-2 gap-12 items-center">
          <div className="relative order-2 md:order-1">
            <div className="absolute -inset-4 bg-[#d4af37]/10 blur-3xl rounded-full" />
            <img src={IMAGES.wellness} alt="Wellness" className="relative rounded-2xl w-full aspect-[4/3] object-cover ring-1 ring-[#d4af37]/30" />
          </div>
          <div className="order-1 md:order-2">
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-4">Wellness & Learn</p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient leading-tight">Crystal Chakra Healing</h2>
            <p className="font-jost text-[#f5ecd9]/70 mt-5 leading-relaxed">A practice focused on the body's energy centers — the chakras — to bring balance and harmony. Using crystals, herbs, and botanical rituals, we cleanse, balance, and restore your energy for greater clarity and wellbeing.</p>
            <Link to="/wellness" className="inline-flex items-center gap-2 mt-8 text-[#f0d582] border-b border-[#d4af37]/50 pb-1 hover:text-[#ff4da6] transition-colors font-jost text-sm tracking-widest uppercase"><BookOpen className="h-4 w-4" /> Learn Your Chakras</Link>
          </div>
        </div>
      </section>

      {/* SACRED AIKO */}
      <section className="py-20 bg-[#0d0a0c]">
        <div className="max-w-5xl mx-auto px-5">
          <div className="relative card-glass rounded-3xl p-10 md:p-14 text-center overflow-hidden">
            <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#d6197d]/15 blur-3xl" />
            <Gamepad2 className="h-12 w-12 text-[#ff4da6] mx-auto mb-5" />
            <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">A Sister Experience</p>
            <h2 className="font-cinzel text-3xl md:text-5xl text-gold-gradient">Sacred Aiko</h2>
            <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-4 max-w-2xl mx-auto">A space within the Prestige Elements universe where energy, elements and interactive discovery meet. Step into the experience and connect it to your Prestige world.</p>
            <Link to="/sacred-aiko" className="btn-gold inline-flex items-center gap-2 mt-8 rounded-full px-9 py-4 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Enter Sacred Aiko <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      {/* OUR STORY */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <p className="font-jost text-xs tracking-[0.4em] text-[#ff4da6] uppercase mb-3">Established 2019</p>
          <h2 className="font-cinzel text-4xl md:text-5xl text-gold-gradient">Our Story</h2>
          <div className="gold-divider mt-4 mb-8" />
          <p className="font-cormorant text-2xl text-[#f5ecd9]/80 leading-relaxed">{ABOUT.heading}</p>
          <p className="font-jost text-[#f5ecd9]/60 leading-relaxed mt-6">{ABOUT.body}</p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-[#f0d582] border-b border-[#d4af37]/50 pb-1 hover:text-[#ff4da6] transition-colors font-jost text-sm tracking-widest uppercase">Read More <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="relative py-20 overflow-hidden bg-[#0d0a0c]">
        <div className="absolute inset-0">
          <img src={IMAGES.spa2} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d0a0c] via-transparent to-[#0d0a0c]" />
        </div>
        <div className="relative max-w-2xl mx-auto px-5 text-center">
          <Sparkles className="h-10 w-10 text-[#f0d582] mx-auto mb-4" />
          <h2 className="font-cinzel text-3xl md:text-4xl text-gold-gradient">Join The Prestige Circle</h2>
          <p className="font-cormorant text-xl text-[#f5ecd9]/70 mt-3">{BRAND.promo}. Sign up for rituals, launches & cosmic offers.</p>
          <Link to="/shop" className="btn-gold inline-flex items-center gap-2 mt-7 rounded-full px-9 py-4 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Start Shopping <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
